// Persistent cache for the community-care provider specialties list.
//
// The list backs the CC provider service-type typeahead and effectively never
// changes, yet it was re-fetched on every page load (and multiple times per load
// via the typeahead's mount + re-render refetch). We cache it in localStorage
// with a short TTL so a reload, a new tab, or a return visit within the window
// skips the /ccp/specialties request entirely, while a stale list still refreshes
// on its own after the TTL.
//
// Uses the platform localStorage wrapper, which falls back to an in-memory shim
// when storage is disabled (private mode, blocked cookies) — so every access is
// still wrapped in try/catch and degrades to an in-memory (session-only) cache
// rather than throwing.
import localStorage from 'platform/utilities/storage/localStorage';

export const CCP_SPECIALTIES_CACHE_KEY = 'facilityLocator.ccpSpecialties';
// 1 day. The list is essentially static; this only bounds how long a rare
// change takes to appear.
export const CCP_SPECIALTIES_TTL_MS = 24 * 60 * 60 * 1000;

export const getCachedSpecialties = () => {
  try {
    const raw = localStorage.getItem(CCP_SPECIALTIES_CACHE_KEY);
    if (!raw) return null;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      localStorage.removeItem(CCP_SPECIALTIES_CACHE_KEY);
      return null;
    }

    const { data, cachedAt } = parsed || {};

    if (
      !Array.isArray(data) ||
      data.length === 0 ||
      typeof cachedAt !== 'number'
    ) {
      localStorage.removeItem(CCP_SPECIALTIES_CACHE_KEY);
      return null;
    }

    if (Date.now() - cachedAt > CCP_SPECIALTIES_TTL_MS) {
      localStorage.removeItem(CCP_SPECIALTIES_CACHE_KEY);
      return null;
    }

    return data;
  } catch (e) {
    // Storage unavailable — treat as a cache miss.
    return null;
  }
};

/**
 * Persists a non-empty specialties array with the current timestamp. Empty or
 * non-array input is ignored so we never cache a failed/blank response. Never
 * throws (best-effort; storage may be full or disabled).
 * @param {Array} data
 */
export const setCachedSpecialties = data => {
  try {
    if (!Array.isArray(data) || data.length === 0) return;
    localStorage.setItem(
      CCP_SPECIALTIES_CACHE_KEY,
      JSON.stringify({ data, cachedAt: Date.now() }),
    );
  } catch (e) {
    // Best-effort cache; ignore quota/availability errors.
  }
};

/** Removes the cached entry. Never throws. */
export const clearCachedSpecialties = () => {
  try {
    localStorage.removeItem(CCP_SPECIALTIES_CACHE_KEY);
  } catch (e) {
    // no-op
  }
};
